// @flow
import Papa from 'papaparse';

export const CSV_TEMPLATE_HEADERS = ['First Name', 'Last Name', 'Phone', 'Email', 'Tags'];

// Sample rows with examples of single and multiple tags
export const SAMPLE_ROWS = [
  {
    'First Name': 'John',
    'Last Name': 'Doe',
    'Phone': '555-123-4567',
    'Email': 'john@example.com',
    'Tags': 'VIP,Preferred Customer'  // Multiple tags comma-separated
  },
  {
    'First Name': 'Jane',
    'Last Name': 'Smith',
    'Phone': '555-987-6543',
    'Email': 'jane@example.com',
    'Tags': 'Standard'  // Single tag
  }
];

// Target field options for column mapping
export const TARGET_FIELDS = [
  { value: '', label: 'Skip this column' },
  { value: 'FirstName', label: 'First Name' },
  { value: 'LastName', label: 'Last Name' },
  { value: 'Phone', label: 'Phone' },
  { value: 'Email', label: 'Email' },
  { value: 'Tags', label: 'Tags' }
];

/**
 * Downloads a CSV template file with example data
 */
export const downloadCsvTemplate = (): void => {
  const csv = Papa.unparse({
    fields: CSV_TEMPLATE_HEADERS,
    data: SAMPLE_ROWS
  });

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'customer_import_template.csv';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Downloads an error report CSV file
 * @param {Array<{row: number, phone: string, reason: string}>} errors - Array of import errors
 */
export const downloadErrorReport = (
  errors: Array<{row: number, phone: string, reason: string}>
): void => {
  const csv = Papa.unparse(errors.map(e => ({
    'Row Number': e.row,
    'Phone': e.phone || '',
    'Error Reason': e.reason
  })));

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'import_errors.csv';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Validates the column mapping
 * Returns an error message if validation fails, or null if valid
 * @param {Object} mapping - The column mapping object
 * @returns {string|null} Error message or null if valid
 */
export const validateColumnMapping = (
  mapping: {[string]: string}
): string | null => {
  // Check if Phone is mapped
  const phoneMapping = Object.entries(mapping).find(([_, value]) => value === 'Phone');
  if (!phoneMapping) {
    return 'Phone column is required. Please map a column to Phone.';
  }

  // Check if at least FirstName or LastName is mapped
  const hasFirstName = Object.entries(mapping).some(([_, value]) => value === 'FirstName');
  const hasLastName = Object.entries(mapping).some(([_, value]) => value === 'LastName');

  if (!hasFirstName && !hasLastName) {
    return 'At least First Name or Last Name is required. Please map a column to First Name or Last Name.';
  }

  return null;
};

/**
 * Formats a file size in bytes to a human-readable string
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Formats a number with commas for display
 * @param {number} num - Number to format
 * @returns {string} Formatted number
 */
export const formatNumber = (num: number): string => {
  return num.toLocaleString();
};
